import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  CalendarDays, MapPin, Users, MessageSquare, Send, Link2, UserPlus, Filter, Clock, X,
} from "lucide-react";
import ScholarAvatar from "@/components/ScholarAvatar";
import ShareMenu from "@/components/ShareMenu";
import VerifiedBadge from "@/components/VerifiedBadge";
import { formatDistanceToNow } from "date-fns";

function StatusPill({ status }) {
  const map = {
    upcoming: { label: "Opens soon", cls: "bg-[color:var(--ayur-cream-2)] text-[color:var(--ayur-mute)]" },
    active:   { label: "Live now",  cls: "bg-[color:var(--ayur-emerald)] text-white" },
    ended:    { label: "Ended",     cls: "bg-[color:var(--ayur-cream-2)] text-[color:var(--ayur-mute)]" },
  };
  const s = map[status] || map.upcoming;
  return <span className={`pill ${s.cls} inline-flex items-center gap-1`}><Clock className="w-3 h-3" />{s.label}</span>;
}

function DiscussionDialog({ event, onClose, onChanged }) {
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteQuery, setInviteQuery] = useState("");
  const [inviteResults, setInviteResults] = useState([]);
  const [inviting, setInviting] = useState({});
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const [m, u] = await Promise.all([
        api.get(`/events/${event.id}/messages`),
        api.get(`/events/${event.id}/members`),
      ]);
      setMessages(m.data);
      setMembers(u.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [event.id]);
  useEffect(() => {
    const t = setInterval(load, 4000); // poll while open
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [event.id]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/events/${event.id}/messages`, { content: text.trim() });
      setText("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  // Live scholar search for invites
  useEffect(() => {
    if (!inviteOpen) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: inviteQuery || "a" } });
        const memberIds = new Set(members.map((m) => m.id));
        setInviteResults((data.users || []).filter((u) => !memberIds.has(u.id)).slice(0, 8));
      } catch { /* silent */ }
    }, 250);
    return () => clearTimeout(t);
  }, [inviteQuery, inviteOpen, members]);

  const invite = async (uid) => {
    if (inviting[uid]) return;
    setInviting((p) => ({ ...p, [uid]: true }));
    try {
      await api.post(`/events/${event.id}/invite`, { user_ids: [uid] });
      toast.success("Invite sent");
      setInviteResults((r) => r.filter((u) => u.id !== uid));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setInviting((p) => ({ ...p, [uid]: false })); }
  };

  const status = event.group_status;
  const canSend = status === "active";
  const placeholder = status === "upcoming"
    ? "Discussion opens 30 min before start…"
    : status === "ended"
    ? "This discussion is closed"
    : "Share your thoughts, notes, or diagnosis…";

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="event-discussion-dialog" className="bg-[color:var(--ayur-cream)] max-w-3xl h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-5 border-b border-[color:var(--ayur-border)] shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {event.reason && <span className="pill">{event.reason}</span>}
                {event.mode && <span className="pill">{event.mode}</span>}
                <StatusPill status={status} />
              </div>
              <DialogTitle className="font-serif-display text-2xl mt-2 text-[color:var(--ayur-emerald)]">{event.title}</DialogTitle>
              <DialogDescription className="text-[color:var(--ayur-mute)] text-xs mt-1">
                {new Date(event.date).toLocaleString()} →{" "}
                {event.end_date ? new Date(event.end_date).toLocaleString() : "—"} · {event.location}
              </DialogDescription>
              {event.meeting_link && (
                <a data-testid="event-meeting-link" href={event.meeting_link} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-xs text-[color:var(--ayur-terracotta)] mt-1 link-underline">
                  <Link2 className="w-3 h-3" /> Join meeting link
                </a>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Chat */}
          <div className="flex-1 flex flex-col min-w-0">
            <div ref={scrollRef} data-testid="event-messages" className="flex-1 overflow-y-auto p-5 space-y-3">
              {messages.length === 0 && (
                <div className="text-center text-sm text-[color:var(--ayur-mute)] py-10">
                  No messages yet. Kick off the discussion with a summary or a question.
                </div>
              )}
              {messages.map((m) => (
                <div key={m.id} data-testid="event-message" className="flex items-start gap-2">
                  <ScholarAvatar user={m.author} size={32} />
                  <div className="min-w-0">
                    <div className="text-xs">
                      <span className="font-semibold text-[color:var(--ayur-ink)]">{m.author?.full_name}</span>
                      {m.author?.verified && <VerifiedBadge size={11} />}
                      <span className="text-[color:var(--ayur-mute)] ml-1">
                        · {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <div className="bg-white rounded-lg px-3 py-2 text-sm text-[color:var(--ayur-ink)] whitespace-pre-wrap mt-0.5 max-w-lg">
                      {m.content}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={send} className="p-4 border-t border-[color:var(--ayur-border)] flex gap-2 shrink-0">
              <Input
                data-testid="event-message-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={!canSend}
                placeholder={placeholder}
                className="bg-white"
              />
              <Button
                data-testid="event-message-send"
                type="submit"
                disabled={!canSend || sending || !text.trim()}
                className="bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>

          {/* Sidebar: members + invite */}
          <aside className="hidden md:flex w-64 border-l border-[color:var(--ayur-border)] flex-col shrink-0">
            <div className="p-4 border-b border-[color:var(--ayur-border)]">
              <div className="label-eyebrow mb-2">Participants ({members.length})</div>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center gap-2">
                    <ScholarAvatar user={m} size={28} />
                    <div className="min-w-0 text-xs">
                      <div className="font-medium truncate inline-flex items-center gap-1">
                        {m.full_name}{m.verified && <VerifiedBadge size={10} />}
                      </div>
                      <div className="text-[color:var(--ayur-mute)] truncate">{m.degree}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <Button
                data-testid="event-open-invite"
                variant="outline"
                size="sm"
                onClick={() => setInviteOpen((v) => !v)}
                className="w-full border-[color:var(--ayur-emerald)] text-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald)] hover:text-white"
              >
                <UserPlus className="w-4 h-4 mr-1" /> {inviteOpen ? "Close invite" : "Invite scholars"}
              </Button>
              {inviteOpen && (
                <div className="mt-3 space-y-2">
                  <Input
                    data-testid="event-invite-search"
                    autoFocus
                    value={inviteQuery}
                    onChange={(e) => setInviteQuery(e.target.value)}
                    placeholder="Search by name / college…"
                    className="bg-white text-sm"
                  />
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {inviteResults.map((u) => (
                      <div key={u.id} className="flex items-center gap-2 text-xs">
                        <ScholarAvatar user={u} size={26} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{u.full_name}</div>
                          <div className="text-[color:var(--ayur-mute)] truncate">{u.college}</div>
                        </div>
                        <Button
                          data-testid={`event-invite-btn-${u.id}`}
                          size="sm"
                          disabled={!!inviting[u.id]}
                          onClick={() => invite(u.id)}
                          className="bg-[color:var(--ayur-gold)] hover:bg-[color:var(--ayur-gold)]/90 text-white h-7 px-2"
                        >
                          Invite
                        </Button>
                      </div>
                    ))}
                    {inviteResults.length === 0 && (
                      <div className="text-xs text-[color:var(--ayur-mute)] text-center py-3">No matches</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Meetups() {
  const { meta } = useAuth();
  const [events, setEvents] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [modes, setModes] = useState([]);
  const [reasonFilter, setReasonFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [openDiscussion, setOpenDiscussion] = useState(null); // event object
  const [form, setForm] = useState({
    title: "", description: "", date: "", end_date: "",
    location: "", reason: "", mode: "Online", meeting_link: "", specialty: "",
  });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/events", {
        params: reasonFilter === "All" ? {} : { reason: reasonFilter },
      });
      setEvents(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    api.get("/meta/meetup").then(({ data }) => { setReasons(data.reasons); setModes(data.modes); });
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reasonFilter]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date || !form.end_date || !form.location || !form.description || !form.reason || !form.mode) {
      toast.error("Please fill all required fields"); return;
    }
    setLoading(true);
    try {
      await api.post("/events", {
        ...form,
        specialty: form.specialty || null,
        meeting_link: form.meeting_link || null,
      });
      toast.success("Meet-up created — attendees can now join the discussion");
      setOpen(false);
      setForm({ title: "", description: "", date: "", end_date: "", location: "", reason: "", mode: "Online", meeting_link: "", specialty: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const toggleRsvp = async (ev) => {
    try {
      if (ev.rsvped_by_me) await api.delete(`/events/${ev.id}/rsvp`);
      else await api.post(`/events/${ev.id}/rsvp`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const groupedByStatus = useMemo(() => {
    const g = { active: [], upcoming: [], ended: [] };
    for (const e of events) (g[e.group_status] || g.upcoming).push(e);
    return g;
  }, [events]);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Meet-ups & discussion groups</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[color:var(--ayur-emerald)] mt-2">
            Discuss a case, host a study group, invite peers
          </h1>
          <p className="text-[color:var(--ayur-ink-2)] mt-2 max-w-2xl">
            Every meet-up doubles as a temporary discussion group. Attendees can chat inside the group
            from 30 minutes before the start until the host's chosen end time.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-event-button" className="bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white rounded-full px-6">
              Host a meet-up
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[color:var(--ayur-cream)] max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">Host a meet-up</DialogTitle>
              <DialogDescription>
                A temporary discussion group opens for everyone who RSVPs during your chosen window.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div>
                <Label>Reason *</Label>
                <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                  <SelectTrigger data-testid="event-reason-select" className="bg-white"><SelectValue placeholder="Why are you meeting?" /></SelectTrigger>
                  <SelectContent>
                    {reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title *</Label>
                <Input data-testid="event-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                       className="bg-white" placeholder="e.g. Chronic Prameha — 42yr male, MP" />
              </div>
              <div>
                <Label>What's on the agenda? *</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                          className="bg-white resize-none"
                          placeholder="Patient history, references, or discussion questions…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start *</Label>
                  <Input data-testid="event-date-input" type="datetime-local"
                         value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-white" />
                </div>
                <div><Label>End *</Label>
                  <Input data-testid="event-end-date-input" type="datetime-local"
                         value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Mode *</Label>
                  <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                    <SelectTrigger data-testid="event-mode-select" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Location / city *</Label>
                  <Input data-testid="event-location-input" value={form.location}
                         onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-white" placeholder="Pune / Zoom / Google Meet" />
                </div>
              </div>
              <div>
                <Label>Meeting link (optional)</Label>
                <Input data-testid="event-meeting-link-input" value={form.meeting_link}
                       onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} className="bg-white"
                       placeholder="https://meet.google.com/… or Zoom URL" />
              </div>
              <div>
                <Label>Specialty (optional)</Label>
                <Select value={form.specialty} onValueChange={(v) => setForm({ ...form, specialty: v })}>
                  <SelectTrigger data-testid="event-specialty-select" className="bg-white"><SelectValue placeholder="Choose" /></SelectTrigger>
                  <SelectContent>
                    {meta.specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="event-submit-button" type="submit" disabled={loading}
                      className="w-full bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)] text-white rounded-full py-6">
                {loading ? "Creating…" : "Create meet-up & open group"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reason filter */}
      <div className="paper-card p-3 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-xs text-[color:var(--ayur-mute)] mr-1">
          <Filter className="w-3 h-3" /> Filter by reason:
        </span>
        <button
          data-testid="reason-filter-all"
          onClick={() => setReasonFilter("All")}
          className={`pill ${reasonFilter === "All" ? "bg-[color:var(--ayur-emerald)] text-white" : ""}`}
        >
          All
        </button>
        {reasons.map((r) => (
          <button
            key={r}
            data-testid={`reason-filter-${r.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
            onClick={() => setReasonFilter(r)}
            className={`pill ${reasonFilter === r ? "bg-[color:var(--ayur-emerald)] text-white" : ""}`}
          >
            {r}
          </button>
        ))}
      </div>

      {["active", "upcoming", "ended"].map((bucket) => {
        const list = groupedByStatus[bucket];
        if (!list?.length) return null;
        const labels = { active: "Live now", upcoming: "Upcoming", ended: "Past meet-ups" };
        return (
          <section key={bucket} data-testid={`events-section-${bucket}`} className="space-y-3">
            <h2 className="font-serif-display text-xl text-[color:var(--ayur-emerald)] flex items-center gap-2">
              {labels[bucket]}
              <span className="text-sm text-[color:var(--ayur-mute)]">· {list.length}</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {list.map((ev) => (
                <div key={ev.id} data-testid="event-card" className="paper-card p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {ev.reason && <span className="pill">{ev.reason}</span>}
                        {ev.mode && <span className="pill">{ev.mode}</span>}
                        {ev.specialty && <span className="pill">{ev.specialty}</span>}
                      </div>
                      <h3 className="font-serif-display text-xl mt-2 text-[color:var(--ayur-ink)] break-words">{ev.title}</h3>
                    </div>
                    <StatusPill status={ev.group_status} />
                  </div>
                  <p className="text-sm text-[color:var(--ayur-ink-2)] line-clamp-3 whitespace-pre-wrap">{ev.description}</p>
                  <div className="text-xs text-[color:var(--ayur-mute)] space-y-1">
                    <div className="flex items-center gap-2">
                      <CalendarDays className="w-3.5 h-3.5" />
                      {new Date(ev.date).toLocaleString()} → {ev.end_date ? new Date(ev.end_date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                    </div>
                    <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />{ev.location}</div>
                    <div className="flex items-center gap-4">
                      <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{ev.rsvp_count} joined</span>
                      <span className="inline-flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />{ev.message_count} msgs</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-[color:var(--ayur-border)]">
                    <div className="flex items-center gap-2">
                      <ScholarAvatar user={ev.host} size={26} />
                      <span className="text-xs text-[color:var(--ayur-mute)]">Hosted by {ev.host?.full_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ShareMenu
                        url={`${window.location.origin}/meetup/${ev.id}`}
                        title={ev.title}
                        body={`${ev.reason} · ${new Date(ev.date).toLocaleString()} · ${ev.location}`}
                        testid="event-share-menu"
                      />
                      {ev.rsvped_by_me ? (
                        <>
                          <Button
                            data-testid="event-open-discussion"
                            size="sm"
                            onClick={() => setOpenDiscussion(ev)}
                            className="bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)] text-white"
                          >
                            <MessageSquare className="w-4 h-4 mr-1" /> Discussion
                          </Button>
                          <Button
                            data-testid="event-leave-button"
                            size="sm" variant="ghost"
                            onClick={() => toggleRsvp(ev)}
                            className="text-[color:var(--ayur-mute)] hover:text-[color:var(--ayur-terracotta)]"
                            title="Leave meet-up"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          data-testid="event-rsvp-button"
                          size="sm"
                          onClick={() => toggleRsvp(ev)}
                          className="bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white"
                        >
                          Join
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {events.length === 0 && (
        <div className="paper-card p-12 text-center">
          <div className="label-eyebrow">Nothing scheduled</div>
          <h3 className="font-serif-display text-2xl mt-2 text-[color:var(--ayur-emerald)]">Be the first to host</h3>
          <p className="text-[color:var(--ayur-ink-2)] mt-2">
            Start a case discussion, a journal club, or a Panchakarma study group.
          </p>
        </div>
      )}

      {openDiscussion && (
        <DiscussionDialog
          event={openDiscussion}
          onClose={() => setOpenDiscussion(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
